import { ReduceStore } from "flux/utils";
import Dispatcher from "Dispatcher";
import invariant from "invariant";
import DataManager from "./DataManager";
import dotProp from "dot-prop-immutable";
import LoadObject from "./LoadObject";
import userStore from "data/userStore";
import SuperRecordActions from "./SuperRecordActions";
import ClientId from "./ClientId";
import SuperRecordListActions from "./SuperRecordListActions";
import { hotLoadObject } from "./storeUtils";
import { superRecordPacket } from "data/selectors/superRecords";
import RecordActions from "data/RecordActions";
import TableStore from "data/TableStore";
import StereotypesStore from "data/StereotypesStore";
import { isConfirmed } from "./OverStore";
import EntityName from "data/util/EntityName";
import NavigatorStore from "data/NavigatorStore";
import { ACTION_CONTEXT_KEY } from "data/util/ContextedReduceStore";
import { canSave } from "data/util/saveit";
import MapButtonActions from "data/MapButtonActions";
import MappingStore from "data/MappingStore";
import MappingActions from "data/MappingActions";
import MapDialogActions from "data/MapDialogActions";
import SearchResultActions from "data/SearchResultActions";
import MergeStore from "data/MergeStore";
import SuperRecordMappingVisitor from "data/SuperRecordMappingVisitor";
import { MERGE_PLUGINS } from "./MergeStore";

const isMagicRootKey = key =>
    ! EntityName.is(key);

const keyPath = key =>
    isMagicRootKey(key) ? "mr" : "tlr";

const path = (action, id = action.id) =>
    [id, keyPath(action.key)];

const addTopLevelId = (state, srKey, srId, tlrKey, tlrId) =>
    dotProp.set(state, [
        srId,
        keyPath(srKey),
    ], lo => lo.map(v => dotProp.set(v, [
        "topLevelIds",
        tlrKey,
    ], ids => Array.isArray(ids) ? ids.concat(tlrId) : [tlrId])));

export const addNew = (state, key, id) =>
    dotProp.set(
        state,
        [id, keyPath(key)],
        LoadObject.withValue({
            id,
            topLevelIds: {},
        }),
    );

const create = (state, key, id, initiatingContext) => {
    const prop = [id, keyPath(key)];
    invariant(
        !dotProp.get(state, prop).isCreating(),
        "RACE! Multiple concurrent requests to create %s#%s.",
        key, id,
    );
    const storeVal = superRecordPacket(
        TableStore,
        StereotypesStore.getStereotypeByKey(key),
        id,
        getLO(state, key, id).getValueEnforcing(),
        userStore.getActiveOrgKey(),
    );
    return dotProp.set(
        state,
        prop,
        LoadObject.withValue(storeVal).creating(
            DataManager.createSuperrecord(
                key,
                id,
                storeVal,
                userStore.getActiveOrgKey(),
                initiatingContext,
            ),
        ),
    );
};

const getLO = (state, key, id) => {
    if (!state.hasOwnProperty(id)) return null;
    const path = keyPath(key);
    if (state[id].hasOwnProperty(path)) return state[id][path];
    return state[id].mr;
};

const _mapping_workflow = state => {
    // ensure MappingStore goes first
    Dispatcher.waitFor([ // todo: get this.dispatcher...
        MappingStore.getDispatchToken(),
        TableStore.getDispatchToken(),
    ]);

    // If MappingStore didn't change, I won't either.
    if (!MappingStore.hasChanged()) return state;

    // If MappingStore isn't ready, wait until it is.
    if (!MappingStore.isReady()) return state;

    /*
     * We are GO!
     */

    return new SuperRecordMappingVisitor(state)
        .execute()
        .getState();
};

const _deleteRecord = (state, action) => {
    // todo: index instead of scanning
    Object.keys(state).forEach(id => {
        if (! state[id].hasOwnProperty("mr")) {
            return;
        }
        if (state[id].mr.isPending()) {
            return;
        }
        const r = state[id].mr.getRequiredValue();
        if (! r.topLevelIds.hasOwnProperty(action.recordKey)) {
            return;
        }
        const i = r.topLevelIds[action.recordKey].indexOf(action.recordId);
        if (i < 0) {
            return;
        }
        state = dotProp.set(state, [id, "mr", ], lo =>
            lo.map(v => dotProp.set(
                v,
                ["topLevelIds", action.recordKey],
                ids =>
                    ids.slice(0, i).concat(ids.slice(i + 1)),
            ))
        );
    });
    return state;
};

class SuperRecordStore extends ReduceStore {

    constructor() {
        super(Dispatcher);
    }

    getInitialState() {
        return {};
    }

    reduce(state, action) {
        switch (action.type) {

            case RecordActions.SET_FIELD: {
                this.__dispatcher.waitFor([
                    NavigatorStore.getDispatchToken(),
                ]);
                if (!NavigatorStore.hasChanged()) return state;
                // nav changed; probably a version change!
                const ep = TableStore.getEntityProxy(
                    action.recordKey,
                    action.recordId,
                );
                const tp = ep.tableProxy;
                const eks = tp.getEntityKeys();
                if (eks[0] === action.recordKey) return state;

                //todo: It's pretty dumb to need to do this munge, but this
                // whole store is a kludge for marionette load flows and should
                // go away at some point. For now... it "works".

                // indeed! do the munge...
                Object.keys(state)
                    .filter(id => state[id].mr != null)
                    .filter(id => state[id].mr.hasValue())
                    .map(id => state[id].mr.getRequiredValue())
                    .filter(mr => mr.topLevelIds != null)
                    .filter(mr => mr.topLevelIds.hasOwnProperty(action.recordKey))
                    .forEach(mr => {
                        state = dotProp.set(state, [mr.id, "mr"], lo =>
                            lo.map(mr => {
                                const prev = mr.topLevelIds[action.recordKey].slice();
                                const idx = prev.indexOf(action.recordId);
                                if (idx < 0) return mr;
                                prev.splice(idx, 1);
                                const next = (mr.topLevelIds[eks[0]] || []).slice();
                                next.push(action.recordId);
                                return {
                                    ...mr,
                                    topLevelIds: {
                                        ...mr.topLevelIds,
                                        [action.recordKey]: prev,
                                        [eks[0]]: next,
                                    }
                                };
                            }));
                    });
                return state;
            }

            case SuperRecordActions.START_LOAD:
                this.__dispatcher.waitFor([
                    userStore.getDispatchToken(),
                ]);
                invariant(
                    !ClientId.is(action.id),
                    "You can't load client-side records (%s#%s) from the server.",
                    action.key,
                    action.id,

                );
                return dotProp.set(state, path(action), LoadObject.load(
                    DataManager.loadSuperrecord(
                        action.key,
                        action.id,
                        userStore.getActiveOrgKey()
                    )
                ));

            case SuperRecordActions.LOADED: {
                state = dotProp.set(
                    state,
                    path(action),
                    lo => lo.receiveValue({
                        id: action.data.id,
                        topLevelIds: action.data.topLevelIds,
                    }),
                );
                state = _mapping_workflow(state);
                return state;
            }

            case SuperRecordListActions.LOADED: {
                if (action.data.length === 0) return state;
                const next = {...state};
                action.data.forEach(r => {
                    const type = keyPath(action.key);
                    const rec = next[r.id] = next.hasOwnProperty(r.id)
                        ? {...next[r.id]}
                        : {};
                    const lo = rec[type] || LoadObject.empty();
                    rec[type] = lo.receiveValue(r);
                });
                return next;
            }

            case SuperRecordActions.LOAD_ERROR: {
                return dotProp.set(state, path(action), lo =>
                    lo.receiveError(action.error));
            }

            case SuperRecordActions.START_CREATE: {
                if (!canSave()) return state;
                this.__dispatcher.waitFor([
                    userStore.getDispatchToken(),
                ]);
                return create(
                    state,
                    action.key,
                    action.id,
                    action[ACTION_CONTEXT_KEY] || action.initiatingContext
                );
            }

            case SuperRecordActions.CREATED: {
                const lo = dotProp.get(state, path(action));
                state = dotProp.delete(state, path(action));
                state = dotProp.set(state, path(action, action.data.id), lo.receiveValue({
                    id: action.data.id,
                    topLevelIds: action.data.topLevelIds,
                }));
                state = _mapping_workflow(state);
                return state;
            }

            case SuperRecordActions.NEW: {
                return addNew(state, action.stereoKey, action.newId);
            }

            case SuperRecordActions.CREATE_ERROR:
                return dotProp.set(state, path(action), lo => lo.receiveError(action.error));

            case SuperRecordActions.START_UPDATE: {
                if (!canSave()) return state;
                if (ClientId.is(action.id)) {
                    throw new Error("Update an unsaved (client-only) superrecord?!");
                }
                this.__dispatcher.waitFor([
                    userStore.getDispatchToken(),
                ]);
                const storeVal = superRecordPacket(
                    TableStore,
                    StereotypesStore.getStereotypeByKey(action.key),
                    action.id,
                    getLO(state, action.key, action.id).getValueEnforcing(),
                    userStore.getActiveOrgKey(),
                );
                return dotProp.set(state, path(action), lo => lo.setValue(storeVal).updating(
                    DataManager.updateSuperrecord(
                        action.key,
                        action.id,
                        storeVal,
                        userStore.getActiveOrgKey(),
                        action[ACTION_CONTEXT_KEY] || action.initiatingContext,
                    )
                ));
            }

            case SuperRecordActions.UPDATED:
                return dotProp.set(state, path(action), lo => lo.receiveValue({
                    id: action.data.id,
                    topLevelIds: action.data.topLevelIds,
                }));
            case SuperRecordActions.UPDATE_ERROR:
                return dotProp.set(state, path(action), lo => lo.receiveError(action.error));
            case SuperRecordActions.DELETE_RECORD:
                if (! isConfirmed(action)) return state;
                this.__dispatcher.waitFor([
                    TableStore.getDispatchToken(),
                    userStore.getDispatchToken(),
                ]);
                return dotProp.set(state, path(action), lo => lo.deleting(
                    DataManager.deleteSuperrecord(
                        action.key,
                        action.id,
                        userStore.getActiveOrgKey(),
                        action[ACTION_CONTEXT_KEY] || action.initiatingContext,
                    )
                ));
            case SuperRecordActions.DELETED:
                return dotProp.delete(state, path(action));
            case SuperRecordActions.DELETE_ERROR:
                return dotProp.set(state, path(action), lo => lo.receiveError(action.error));
            case RecordActions.NEW:
                return dotProp.set(state, [
                    action.newId,
                    keyPath(action.recordKey),
                ], LoadObject.withValue({
                    id: action.newId,
                    topLevelIds: {},
                }));

            case SuperRecordActions.NEW_MAGIC_CHILD: {
                return addTopLevelId(
                    state,
                    action.recordKey,
                    action.recordId,
                    action.entityKey,
                    action.newId,
                );
            }

            case MapButtonActions.AUTOMAP_TO_NEW_MAGIC_CHILD: {
                const magic = NavigatorStore.getMagicState(action[ACTION_CONTEXT_KEY]);
                return addTopLevelId(
                    state,
                    magic.key,
                    magic.id,
                    action.entityKey,
                    action.newId,
                );
            }

            case MapButtonActions.AUTOMAP_FROM:
            case MappingActions.MODEL_LOADED:
            case MapDialogActions.TOGGLE_EXCLUSION:
            case MapDialogActions.SET_MAGIC_ROOT_EXCLUSION:
            case MapDialogActions.IMPORT:
            case SearchResultActions.SEARCH_RESULT:
            case MappingActions.__DO_IT_AGAIN: {
                return _mapping_workflow(state);
            }

            case MappingActions.IT_IS_DONE: {
                if(MergeStore.isReady()) return state; // not merging anything
                // wait for merge to be processed then delete.
                this.__dispatcher.waitFor([
                    TableStore.getDispatchToken(),
                ]);
                const mergeType = MergeStore.getPluginKey();

                if(mergeType === MERGE_PLUGINS.RECORD_MERGE) {
                    const action = MergeStore.getDeleteAction();
                    return _deleteRecord(state, action);
                }
                else if(mergeType === MERGE_PLUGINS.GLOBAL_MERGE) {
                    // now we need to save the target record.
                    this.__dispatcher.waitFor([
                        userStore.getDispatchToken(),
                    ]);
                    const mergeState = MergeStore.getState();
                    const updateAction = {
                        key: mergeState.target.stereoKey,
                        id: mergeState.target.id,
                        sourceId: mergeState.source.id,
                    };

                    const storeVal = superRecordPacket(
                        TableStore,
                        StereotypesStore.getStereotypeByKey(updateAction.key),
                        updateAction.id,
                        getLO(state, updateAction.key, updateAction.id).getValueEnforcing(),
                        userStore.getActiveOrgKey(),
                    );
                    return dotProp.set(state, path(updateAction), lo => lo.setValue(storeVal).updating(
                        DataManager.mergeSuperrecord(
                            updateAction.key,
                            updateAction.id,
                            updateAction.sourceId,
                            storeVal,
                            userStore.getActiveOrgKey(),
                            action.initiatingContext,
                        )
                    ));
                }
                return state;
            }

            case RecordActions.DELETE_RECORD:
                if (! isConfirmed(action)) return state;
                return _deleteRecord(state, action);
            default:
                return state;
        }
    }

    isUnsaved(key, id) {
        const lo = getLO(this.getState(), key, id);
        return lo != null && !lo.hasOperation();
    }

    getRecord(key, id) {
        return this.getRecordLO(key, id).getValueEnforcing();
    }

    getRecordLO(key, id, forceRefresh) {
        return hotLoadObject(
            () => getLO(this.getState(), key, id),
            () => Dispatcher.dispatch({
                type: SuperRecordActions.START_LOAD,
                key,
                id,
            }),
            lo => !lo.isPending() && (forceRefresh || lo.isEmpty()),
        );
    }
}

export default new SuperRecordStore();



// WEBPACK FOOTER //
// ./app/data/SuperRecordStore.js